import React, { Component } from "react";
import { WAVES, LATEST_WAVE } from "./constants/waves";
import { withRouter } from "./withRouter";
import mapboxgl from "maplibre-gl";
import { tableFromIPC } from "apache-arrow";
import { Box,
  ButtonBase, 
  Typography} from "@mui/material";
import MapComponent from "./MapComponent"
import { alpha } from '@mui/material/styles';
import ConstituencyGrid from "./ConstituencyGrid"
import NationalVoteBar from "./NationalVoteBar"
import VoteLineCharts from "./VoteLineCharts"
import CoalitionTrackingBox from "./CoalitionTrackingBox"
import { motion, LayoutGroup,AnimatePresence } from "framer-motion";
import CoalitionPerformanceBox from "./CoalitionPerformanceBox"
import { precomputeCentroids, buildCompareArrows } from "./buildCompareArrows";
import { rowsFromTable } from "./utils/arrowUtils";
import { baseColorForLikertKey } from "./utils/styleUtils";
import { PARTY_INFO, COMPARE_PARTY_OPTIONS } from "./constants/partyConfig";
import { SEGMENT_INFO, COMPARE_SEGMENT_OPTIONS, buildLabelsAndColors } from "./constants/segmentConfig";
import Tooltip from "@mui/material/Tooltip";
// Reusable pill switcher component
const PillSwitcher = ({ label, options, value, onChange, layoutId }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <LayoutGroup id={layoutId}>
      <Box sx={{ display: "flex", gap: "2px", background: "rgba(0,0,0,0.06)", borderRadius: 1.5, p: "3px" }}>
        {options.map((opt) => {
          const optValue = opt.value ?? null;
          const isActive = value === optValue;
          return (
            <ButtonBase
              key={opt.label}
              onClick={() => onChange(optValue)}
              sx={{
                position: "relative",
                px: 1.2,
                py: 0.35,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#fff" : "text.secondary",
                zIndex: isActive ? 1 : 0,
                transition: "color 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {isActive && (
                <motion.div
                  layoutId={layoutId}
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 4,
                    background: "#1976d2",
                  }}
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <span style={{ position: "relative", zIndex: 1 }}>{opt.label}</span>
            </ButtonBase>
          );
        })}
      </Box>
    </LayoutGroup>
  </Box>

);

const MotionBox = motion(Box);

const SegmentTreeMap = ({ metricRows, selectedFeature, waveSelect }) => {
  const segmentKeys = [
    "PragCentre_n",
    "ProgIdeal_n",
    "Disillusioned_n",
    "PopRight_n",
    "Trads_n",
  ];

 // const areaFilter = selectedFeature?.area_id ? String(selectedFeature.area_id) : "National";
  const areaLabel = selectedFeature?.area_name || "National";

  if (!metricRows?.length) return null;

  const relevantRows = metricRows.filter(
    (r) =>
    //  String(r.area_id) === areaFilter &&
      Number(r.wave) === Number(waveSelect)
  );

  if (!relevantRows.length) return null;

  const totalWeight = relevantRows.reduce(
    (sum, r) => sum + (Number(r.turnout_1_n_sum) || 0),
    0
  );

  if (totalWeight === 0) return null;

  const segments = segmentKeys
    .map((k) => {
      const weightedValue =
        relevantRows.reduce((sum, r) => {
          const value = Number(r[k]) || 0;
          const weight = Number(r.turnout_1_n_sum) || 0;
          return sum + value * weight;
        }, 0) / totalWeight;

      return {
        key: k,
        label: SEGMENT_INFO[k]?.name || k,
      description: SEGMENT_INFO[k]?.description || "",
        value: weightedValue,
        color: SEGMENT_INFO[k]?.color || "#666",
      };
    })
    .sort((a, b) => b.value - a.value);

  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  const topRow = segments.slice(0, 2);
  const bottomRow = segments.slice(2);
  const topTotal = topRow.reduce((s, seg) => s + seg.value, 0);
  const bottomTotal = bottomRow.reduce((s, seg) => s + seg.value, 0);

 const TreeCell = ({ seg, pct }) => (
  <Tooltip
    arrow
    placement="left"
    title={
      <Box sx={{ px: 0.5, py: 0.25 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {seg.label}
        </Typography>
        {seg.description ? (
          <Typography sx={{ fontSize: "0.85rem", opacity: 0.95 }}>
            {seg.description}
          </Typography>
        ) : null}
        {/* <Typography sx={{ fontSize: "0.75rem", mt: 0.5, fontWeight: 600 }}>
          {pct.toFixed(1)}%
        </Typography> */}
      </Box>
    }
  >
    <MotionBox
      layout
      transition={{
        layout: { duration: 0.75, ease: "easeInOut" },
        opacity: { duration: 0.75 },
      }}
      sx={{
        flex: `${seg.value} 1 0`,
        minWidth: 0,
        minHeight: 0,
        bgcolor: seg.color,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: { xs: 0.5, sm: 1 },
        py: 0.5,
        overflow: "hidden",
        cursor: "help",
      }}
    >

  <Typography
    sx={{
      fontWeight: 800,
      fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
      color: "#fff",
      lineHeight: 1.1,
    }}
  >
    {pct.toFixed(1)}%
  </Typography>

      <Typography
        sx={{
          fontSize: { xs: "1rem", sm: "1.25rem" },
          fontWeight: 600,
          color: "rgba(255,255,255,0.88)",
          mt: 0.25,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {seg.label}
      </Typography>
    </MotionBox>
  </Tooltip>
);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        px: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5,
          display: "block",
          flexShrink: 0,
        }}
      >
        Segment composition — {areaLabel}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        {/* Top row */}
        <MotionBox
  layout
  sx={{
    display: "flex",
    gap: "2px",
    height: `${(topTotal / total) * 100}%`,
    minHeight: { xs: 80, sm: 100 },
  }}
>
  {topRow.map((seg) => (
    <TreeCell key={seg.key} seg={seg} pct={(seg.value / total) * 100} />
  ))}
</MotionBox>

<MotionBox
  layout
  sx={{
    display: "flex",
    gap: "2px",
    height: `${(bottomTotal / total) * 100}%`,
    minHeight: { xs: 90, sm: 110 },
  }}
>
  {bottomRow.map((seg) => (
    <TreeCell key={seg.key} seg={seg} pct={(seg.value / total) * 100} />
  ))}
</MotionBox>
    </Box>
    </Box>
  );
};
const THEME_CONFIGS  ={
  latest:{ label:"Latest Wave"},
  compare:{label:"Tracking"}
}


class SegmentPage extends Component {
      constructor(props) {
    super(props);
    this.state = {
      
      selectedSection: "latest",
      geoLevel: "constituency",
      metricRows: null, 
      loading: false,
      hoverInfo: null,
      hoverAreaId: null,
      selectedStateBounds: null,
      selectedFeature: null,   
      geoData:props.constituencyMap,
      styleLoaded: false,
      viewState: null,
      metricRowsConst: null,
      metricRowsNational:null,
        chosenGender:null,
      chosenAge:null,
      chosenEthnicity:null,
      chosenEducation:null,
      chosenPV:null,
  comparePartyKey: "PragCentre_n", 
      compareWaves: false,
      waveOptions: WAVES,
      waveSelect: LATEST_WAVE
    };
    
    this.mapRef = React.createRef();
    this._inflight = null; 
    this._lastKey = null;
    this._lastAt = 0;

    
  }

  
  componentDidMount() {
  const centroids = precomputeCentroids(this.props.constituencyMap?.features);
  this.setState({ centroids });

  const run = async () => {
    await this.fetchMetrics("national", null, true);
    await this.fetchMetrics("constituency", null, true);
  };
  run();
}

  componentDidUpdate(prevProps, prevState) {
  const groupChanged   = prevProps.selectedGroupId !== this.props.selectedGroupId;
  const levelChanged   = prevState.geoLevel !== this.state.geoLevel;

   const chosenGenderChanged      = prevState.chosenGender !== this.state.chosenGender;
    const chosenAgeChanged      = prevState.chosenAge !== this.state.chosenAge;
    const chosenEthnicityChanged      = prevState.chosenEthnicity !== this.state.chosenEthnicity;
    const chosenEducationChanged      = prevState.chosenEducation !== this.state.chosenEducation;
    
    const chosenGenderPVChanged      = prevState.chosenPV !== this.state.chosenPV;
  const waveChanged    = prevState.waveSelect !== this.state.waveSelect;
    const filtersChanged = chosenGenderChanged || chosenAgeChanged || chosenEthnicityChanged ||chosenGenderPVChanged ||chosenEducationChanged


  
    const needRefetch = levelChanged  || groupChanged ||filtersChanged;
    if (needRefetch) {
        const run = async () => {
    //await this.fetchMetrics("constituency", null, true);
    await this.fetchMetrics(this.state.geoLevel, this.state.selectedFeature?.area_id);
    };
    run();
    }

  
    const rowsChanged  = prevState.metricRows !== this.state.metricRows;
    const geoChanged   = prevState.geoData !== this.state.geoData;
    const chosenHeatPartyChanged   = prevState.chosenHeatParty !== this.state.chosenHeatParty;
    const chosenHeatStyleChanged   = prevState.chosenHeatStyle !== this.state.chosenHeatStyle;
    const areaChanged = prevState.selectedFeature !== this.state.selectedFeature
  
    if (geoChanged || rowsChanged || groupChanged||filtersChanged||chosenHeatPartyChanged||chosenHeatStyleChanged||waveChanged ||areaChanged) {
      this.restyle();
    }
  


  const compareChanged = prevState.compareWaves !== this.state.compareWaves;  // ← was showArrows

const partyKeyChanged = prevState.comparePartyKey !== this.state.comparePartyKey;

  // buildArrows needs to fire when compare mode toggles, data changes, or geo changes
  if (geoChanged || rowsChanged || compareChanged || waveChanged||partyKeyChanged) {
    this.buildArrows();
  }
}
  _inflightBaseline = null;
  _lastBaselineKey = null;
  _lastBaselineAt = 0;
    requestKey = (level) => {

    return `${level}`;
  };


  //main function that grabs data
    fetchMetrics = async (level, constCode, save_const_results) => {
  const controller = new AbortController(); // new controller for this call
  const signal = controller.signal;

  const reqKey = this.requestKey(level);
  const now = Date.now();
  if (this._lastKey === reqKey && now - this._lastAt < 700) return;
  this._lastKey = reqKey;
  this._lastAt = now;


    let keysBody 
    if(level==="national"||save_const_results){
          keysBody= [
                    "nat_pred_n",
                    "lab_pred_n",
                    "act_pred_n",
                    "nzf_pred_n",
                    "green_pred_n",
                    "tpm_pred_n",
                    "top_pred_n",
                    "oth_pred_n",
                    "inflation_issue_n",
                    "health_issue_n",
                    "econ_issue_n",
                    "crime_issue_n",
                    "housing_issue_n",
                    "poverty_issue_n",
                    "unemployment_issue_n",
                    "climate_issue_n",
                    "education_issue_issue_n",
                    "transport_issue_n",
                    "drugs_issue_n",
                    "race_rel_issue_n",
                    "tax_issue_n",
                    "pollution_issue_n",
                    "maori_issue_n",
                    "immigration_issue_n",
                    "defence_issue_n",
                    "gov_good_n",
                    "gov_bad_n",
                    "gov_dn_n",
                    "coalition_consider", 
                    "coalition_left_consider", 
                    "nat_consider", 
                    "act_consider", 
                    "nzf_consider", 
                    "lab_consider", 
                    "green_consider", 
                    "tpm_consider", 
                    "top_consider", 
                    "turnout_1_p",
                "PragCentre_n",
                    "ProgIdeal_n",
                    "Disillusioned_n",
                    "PopRight_n",
                        "Trads_n"


                  ]  
    }else{
    keysBody= [
                          "PragCentre_n",
                     "ProgIdeal_n",
                      "Disillusioned_n",
                       "PopRight_n",
                          "Trads_n",
                    "turnout_1_p"
                  ]
                }
                
    let turnout_weight
    turnout_weight = "turnout_1_n"
    const body = {
      groupKeys: keysBody,
        filters: {
      gender: this.state.chosenGender ?? null,     
      age: this.state.chosenAge ?? null,            
      ethnicity: this.state.chosenEthnicity ?? null,
      education: this.state.chosenEducation ??null,
      vote_2023: this.state.chosenPV ?? null,     
    },
      constCode: constCode,
    turnout_weight: turnout_weight

    };
  
    this.setState({ loading: true });
  

 
    try {
      // --- call main metrics endpoint ---
          const resMain = await fetch(`/api/${level}_data_arrow/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
                signal,
                });
      if (!resMain.ok) throw new Error(`metrics fetch ${resMain.status}`);
      const bufMain = await resMain.arrayBuffer();
      const tableMain = tableFromIPC(bufMain);
      const rowsMain = rowsFromTable(tableMain);
      // --- update state with both ---
      if(save_const_results){
        if(level==="national"){
          this.setState(
             { metricRowsNational: rowsMain })
            }else{
            this.setState(
            { metricRowsConst: rowsMain ,
                metricRows: rowsMain,})
            }
      }else{
           this.setState({
        metricRows: rowsMain,
        loading: false,
        
      });
      }
   
    } catch (e) {
      if (e.name !== "AbortError") console.error("metrics error:", e);
      this.setState({ metricRows: null,  loading: false });
    } finally {
      this._inflight = null;
    }

  }


  buildArrows = () => {
  const { geoData, metricRows, waveSelect, waveOptions, compareWaves, comparePartyKey } = this.state;
  if (!compareWaves || !metricRows?.length || !geoData?.features?.length) {
    this.setState({ compareArrows: null });
    return;
  }

  // Get the two most recent waves
  const sorted = [...waveOptions].sort((a, b) => b - a);
  if (sorted.length < 2) {
    this.setState({ compareArrows: null });
    return;
  }
  const [latestWave, prevWave] = sorted;

  const latestRows = metricRows.filter((r) => Number(r.wave) === Number(latestWave));
  const prevRows   = metricRows.filter((r) => Number(r.wave) === Number(prevWave));

  // Build delta rows: latest minus previous for each area
  const prevById = new Map(prevRows.map((r) => [String(r.area_id), r]));
  
  const valueKey = comparePartyKey || "PragCentre_n"; // or whichever column you want arrows for
 
  const deltaRows = latestRows.map((latest) => {
    const id   = String(latest.area_id);
    const prev = prevById.get(id);
    const lv   = Number(latest[valueKey]);
    const pv   = Number(prev?.[valueKey]);
    return {
      area_id: id,
      [valueKey]: (Number.isFinite(lv) ? lv : 0) - (Number.isFinite(pv) ? pv : 0),
    };
  });
  const partyColor = SEGMENT_INFO[valueKey]?.color || "#15803d";

  const compareArrows = buildCompareArrows({
  centroidMap: this.state.centroids,  // precomputed, no geometry work
  deltaRows,
  valueKey,
    posColor: partyColor,       // party colour for gains
    negColor: "#9ca3af",        // neutral grey for losses
});

  this.setState({ compareArrows });
};

  restyle = () => {
  const { geoData, metricRows } = this.state;

  if (!geoData) {
    this.setState({ styledGeo: null });
    return;
  }
  if (!metricRows) {
    return;
  }

  const currentWave = Number(this.state.waveSelect);
  const prevWave = currentWave - 1;

  // Current wave lookup
  const filteredMetricRows = metricRows.filter(
    (g) => Number(g.wave) === currentWave
  );
  const byId = new Map();
  for (const r of filteredMetricRows) {
    const id = String(r.area_id ?? "");
    if (id) byId.set(id, r);
  }

  // Previous wave lookup (empty map if wave 1)
  const prevById = new Map();
  if (prevWave >= 1) {
    const prevRows = metricRows.filter(
      (g) => Number(g.wave) === prevWave
    );
    for (const r of prevRows) {
      const id = String(r.area_id ?? "");
      if (id) prevById.set(id, r);
    }
  }

  const { labels, colors } = buildLabelsAndColors();

  const groupKeys = [
    "PragCentre_n",
                     "ProgIdeal_n",
                      "Disillusioned_n",
                       "PopRight_n",
                          "Trads_n",
  ];

  const noDataProps = {
    fillColor: "#ffffff",
    topLabel: "No data",
    topValue: 0,
    _allValues: {},
    _allValuesSorted: [],
    _diffValues: {},
    _diffValuesSorted: [],
    _hasPrevWave: prevWave >= 1 && prevById.size > 0,
  };

  const styled = {
    type: "FeatureCollection",
    features: geoData.features.map((f) => {
      const rid = String(f.properties?.area_id ?? "");
      const row = byId.get(rid);
      const prevRow = prevById.get(rid);

      if (!row) {
        return {
          ...f,
          properties: { ...f.properties, ...noDataProps },
        };
      }

      const displayKeys = groupKeys.filter((k) => k in row);
      if (displayKeys.length === 0) {
        return {
          ...f,
          properties: { ...f.properties, ...noDataProps },
        };
      }

      // --- Current wave entries (unchanged logic) ---
      const entries = displayKeys
        .map((k) => ({
          key: k,
          area_name: labels[k] || k,
          area_id: Number(row[k]),
        }))
        .filter((e) => Number.isFinite(e.area_id))
        .sort((a, b) => b.area_id - a.area_id);

      let fillColor = "#666";
      const topKey = entries[0]?.key;
      const topLabel = entries[0]?.area_name || "";
      const topValue = entries[0]?.area_id || 0;
      fillColor = colors[topKey] || "#000000";

      const sortedvals = entries
        .map((e) => ({
          ...e,
          order: e.key === "Net" ? Number.MAX_SAFE_INTEGER : 0,
        }))
        .sort((a, b) => a.order - b.order)
        .map((e) => ({ key: e.key, area_name: e.area_name, area_id: e.area_id }));

      // --- Wave-on-wave diffs ---
      let diffEntries = [];
      if (prevRow) {
        diffEntries = displayKeys
          .map((k) => {
            const curr = Number(row[k]);
            const prev = Number(prevRow[k]);
            const diff =
              Number.isFinite(curr) && Number.isFinite(prev)
                ? curr - prev
                : null;
            return {
              key: k,
              area_name: labels[k] || k,
              area_id: diff, // keeps the same shape as entries
            };
          })
          .filter((e) => e.area_id !== null);
      }

      const diffSorted = diffEntries
        .map((e) => ({
          ...e,
          order: e.key === "Net" ? Number.MAX_SAFE_INTEGER : 0,
        }))
        .sort((a, b) => a.order - b.order)
        .map((e) => ({ key: e.key, area_name: e.area_name, area_id: e.area_id }));

      return {
        ...f,
        properties: {
          ...f.properties,
          fillColor,
          topKey,
          topLabel,
          topValue,
          _allValues: Object.fromEntries(
            entries.map((e) => [e.key, e.area_name, e.area_id])
          ),
          _allValuesSorted: sortedvals,
          Turnout: row?.turnout_1_p,
_diffTurnout: prevRow && row?.turnout_1_p != null && prevRow?.turnout_1_p != null
  ? Number(row.turnout_1_p) - Number(prevRow.turnout_1_p)
  : null,
          // New diff properties
          _diffValues: Object.fromEntries(
            diffEntries.map((e) => [e.key, e.area_name, e.area_id])
          ),
          _diffValuesSorted: diffSorted,
          _hasPrevWave: !!prevRow,
        },
      };
    }),
  };

  this.setState({ styledGeo: styled, styleLoaded: true });
};

  fitBoundsFromFeatures = (features) => {
  if (!this.mapRef.current || !features?.length) return null;
  const map = this.mapRef.current.getMap();
  const bounds = new mapboxgl.LngLatBounds();
  this.setState({viewState:null})
  features.forEach(f => {
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates.flat(1)
      : f.geometry.coordinates.flat(2);
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  map.fitBounds(bounds, { padding: 80, duration: 500 });
  return bounds;
};

  

   onMapClick = (evt) => {
    const feature = evt.features?.[0];
    if (!feature) return;
    const level = this.state.geoLevel;
    const p = feature.properties || {};
    const areaId = String(p.area_id ?? "");
    const map = this.mapRef.current?.getMap();


    const zoomToFeature = (feat) => this.fitBoundsFromFeatures([feat]);

    

    const clickedLevel = level;

    if (
      areaId &&
      this.state.selectedFeature &&
      this.state.selectedFeature.level === clickedLevel &&
      this.state.selectedFeature.area_id === areaId
    ) {
      this.setState({ selectedFeature: null }, () => {
        if (this.state.selectedStateBounds) {
          map?.fitBounds(this.state.selectedStateBounds, { padding: 80, duration: 500 });
        } else {
         // map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
        }

        this.props.onFeatureSelect?.({
          feature: null,
          selectedAreaLabel: null,
          selectedFeatureLevel: null,
        });
      });
      return;
    }

     if (level === "constituency") {
      const constCode = String(p.area_id || "")
      if (!constCode) return;
  
      const stateBounds = zoomToFeature(feature); // sets map to state
      const stateLabel =  p.area_name;
  
      this.setState(
        {
          selectedconstCode: constCode,
          selectedStateBounds: stateBounds,
          geoLevel: "SA3",
          // Keep selectedFeature pointing to the state context
          selectedFeature: {
            level: "constituency",
            key: "STATEFP",
            area_id: constCode,
            area_name: stateLabel,
            rowMatchIds: [constCode],
          },
        },
        () => {
          // Make sure county metrics (and secondary metrics) are loaded for tooltips
          this.fetchMetrics("SA3",constCode );
          // Load county geo for that state (no auto-fit; we already zoomed)
          this.loadGeo("SA3", { doFit: false });
                
  
        }
      );
      return;
    }


  };

    onMouseMove = (evt) => {
        const feature = evt.features && evt.features[0];
        if (!feature) return this.setState({ hoverInfo: null,hoverAreaId:null });
    
        const { lng, lat } = evt.lngLat; 
        this.setState({
        hoverInfo: { lon: lng, lat, props: feature.properties },
        hoverAreaId: feature.properties.area_id ?? null
        });
    };

  loadGeo = async (level) => {
    let newGeo 
    switch(level){
        case "constituency":
         newGeo = this.props.constituencyMap
        break
        case "SA3":
         newGeo = this.props.SA3Map
        break
        case "SA2":
         newGeo = this.props.SA2Map
        break

        
        default:
         newGeo = this.props.constituencyMap
        break
    }
    this.setState({ geoData: newGeo, geoLevel: level }, () => {
  const centroids = precomputeCentroids(newGeo.features);
  this.setState({ centroids });
});

  }

 onGeoLevelChange = (newLevel) => {
  if (!newLevel) return;

  // if these are async functions already, await them
  this.fetchMetrics(newLevel, this.state.selectedFeature?.area_id);
  this.loadGeo(newLevel);

  // return when both finish
};
    // Utility: compute bounds [minX, minY, maxX, maxY] from a FeatureCollection
getFeatureCollectionBounds = (featureCollection) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  featureCollection.features.forEach((feat) => {
    const coords = feat.geometry.coordinates;

    const recurse = (arr) => {
      if (typeof arr[0] === "number") {
        const [x, y] = arr;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      } else {
        arr.forEach(recurse);
      }
    };

    recurse(coords);
  });

  return [[minX, minY], [maxX, maxY]];
};
    

   resetView = ({ LevelToResetTo = "constituency" }) => {
    const map = this.mapRef.current?.getMap();
      if (map) {
    map.fitBounds(
      [
        [166.0, -47.5], // SW
        [179.0, -33.0], // NE
      ],
      { padding: 80 }
    );
  }

  this.setState(
    {
      geoLevel: LevelToResetTo,
      selectedStateCode: null,
      selectedStateBounds: null,
      selectedFeature: null,
      metricRows: null,
      styledGeo: null,
      
    },
    () => {
      this.onGeoLevelChange(LevelToResetTo);

    }
  );
};

handleselectedFeatureChange =  (area_id, area_name) => {



    const feature = this.props.constituencyMap.features.filter(
          f => String(f.properties.area_id) === String(area_id.area_id)
        )[0];
    if (!feature) return;
    const level = this.state.geoLevel;
    const p = feature.properties || {};
    const areaId = String(p.area_id ?? "");
    const map = this.mapRef.current?.getMap();

    const zoomToFeature = (feat) => this.fitBoundsFromFeatures([feat]);   

    const clickedLevel = level;


    if (
      areaId &&
      this.state.selectedFeature &&
      this.state.selectedFeature.level === clickedLevel &&
      this.state.selectedFeature.area_id === areaId
    ) {
      this.setState({ selectedFeature: null }, () => {
        if (this.state.selectedStateBounds) {
          map?.fitBounds(this.state.selectedStateBounds, { padding: 80, duration: 500 });
        } else {
         // map?.fitBounds([[-125, 24], [-66, 49]], { padding: 20, duration: 500 });
        }

        this.props.onFeatureSelect?.({
          feature: null,
          selectedAreaLabel: null,
          selectedFeatureLevel: null,
        });
      });
      return;
    }
      const stateBounds = zoomToFeature(feature); // sets map to state

      const constCode = String(p.area_id || "")
      if (!constCode) return;
  
      const stateLabel =  p.area_name;
  
      this.setState(
        {
          selectedconstCode: constCode,
          selectedStateBounds: stateBounds,
          geoLevel: "SA3",
          viewState:null,
          // Keep selectedFeature pointing to the state context
          selectedFeature: {
            level: "constituency",
            key: "area_id",
            area_id: constCode,
            area_name: stateLabel,
            rowMatchIds: [constCode],
            
          },
        },
        () => {
          // Make sure county metrics (and secondary metrics) are loaded for tooltips
          this.fetchMetrics("SA3",constCode );
          // Load county geo for that state (no auto-fit; we already zoomed)
          this.loadGeo("SA3", { doFit: false });
                
  
        }
      );
      return;
    

    
}


 handlechosenGenderChange     = (val) => this.setState({ chosenGender: val });
 handlechosenAgeChange     = (val) => this.setState({ chosenAge: val });
 handlechosenEthnicityChange     = (val) => this.setState({ chosenEthnicity: val });
 handlechosenEducationChange     = (val) => this.setState({ chosenEducation: val });
 handlechosenPVChange     = (val) => this.setState({ chosenPV: val });
getLatestWave = (metricRows) => {
  if (!metricRows?.length) return null;
  const latest = metricRows.reduce((a, b) => (Number(a.wave) > Number(b.wave) ? a : b));
  return { latest_wave: latest.wave, latest_wave_date: latest.wave_date };
}

  handleSectionChange     = (val) => this.setState({ selectedSection: val });


  handleCompareWavesChange = (val) => this.setState({ compareWaves: val });
  handlewaveSelectChange     = (val) => {
    
    this.setState({ waveSelect: val })
    
  };

  render() {
  
const { latest_wave, latest_wave_date } = this.getLatestWave(this.state.metricRowsConst) || {};


  return(
<Box
  sx={{
    display: "flex",
    height: "100%",      // own the height here
    width: "100%",
    overflow: "hidden",  // no page scrollbar
  }}
>
  {/* LEFT COLUMN */}
  <Box
    component="main"
    sx={(theme) => ({
      flex: 1,
      minWidth: 0,             // important for flex children
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: theme.vars
        ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
        : alpha(theme.palette.background.default, 1),
    })}
  >
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <MapComponent
            mapRef={this.mapRef}
            styledGeo={this.state.styledGeo}
            geoData={this.state.geoData}
            hoverInfo={this.state.hoverInfo}
            styleLoaded={this.state.styleLoaded}
            selectedFeature={this.state.selectedFeature}
            onMapClick={this.onMapClick}
            onMouseMove={this.onMouseMove}
            onGeoLevelChange={this.onGeoLevelChange}
            buildLabelsAndColors={buildLabelsAndColors}
            geoLevel={this.state.geoLevel}
            resetView={this.resetView}
            handleselectedFeatureChange={this.handleselectedFeatureChange}
            viewState={this.state.viewState}
            tooltip={"segments"}
            compareWaves={this.state.compareWaves}
  onCompareWavesChange={this.handleCompareWavesChange}
  
                waveSelect={this.state.waveSelect}
                handlewaveSelectChange={this.handlewaveSelectChange}
                waveOptions={this.state.waveOptions}
                allowCompare={true}
  compareArrows={this.state.compareArrows}
  showArrows={this.state.showArrows}
  onShowArrowsToggle={() => this.setState((s) => ({ showArrows: !s.showArrows }))}
  comparePartyKey={this.state.comparePartyKey}
  onComparePartyKeyChange={(val) => this.setState({ comparePartyKey: val })}
  comparePartyOptions={COMPARE_SEGMENT_OPTIONS}
  showWaveBox={true}
        />
        </Box>
    </Box>

   {/* RIGHT COLUMN */}
<Box
  component="main"
  sx={{
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "hidden", // important
    height: "100%",
    px: 1,
    pt: 0.5,
    pb: 1,
  }}
>
  {/* Controls */}
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      flexShrink: 0,
      mb: 0.5,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 2,
        rowGap: 1,
        px: 2,
        py: 0.75,
        borderRadius: 3,
        bgcolor: "rgba(0,0,0,0.04)",
      }}
    >
      <PillSwitcher
        label="Gender"
        layoutId="gender"
        value={this.state.chosenGender}
        onChange={(v) => this.handlechosenGenderChange?.(v)}
        options={[
          { value: null, label: "All" },
          { value: "M", label: "Male" },
          { value: "F", label: "Female" },
        ]}
      />

      <Box sx={{ width: "1px", height: 20, bgcolor: "rgba(0,0,0,0.12)" }} />

      <PillSwitcher
        label="Age"
        layoutId="age"
        value={this.state.chosenAge}
        onChange={(v) => this.handlechosenAgeChange?.(v)}
        options={[
          { value: null, label: "All" },
          { value: "Young", label: "Young" },
          { value: "Middle", label: "Middle" },
          { value: "Old", label: "Old" },
        ]}
      />

      <Box sx={{ width: "1px", height: 20, bgcolor: "rgba(0,0,0,0.12)" }} />

      <PillSwitcher
        label="Education"
        layoutId="education"
        value={this.state.chosenEducation}
        onChange={(v) => this.handlechosenEducationChange?.(v)}
        options={[
          { value: null, label: "All" },
          { value: "High", label: "High" },
          { value: "Mid", label: "Mid" },
          { value: "Low", label: "Low" },
        ]}
      />

      <Box sx={{ width: "1px", height: 20, bgcolor: "rgba(0,0,0,0.12)" }} />

      <PillSwitcher
        label="Ethnicity"
        layoutId="ethnicity"
        value={this.state.chosenEthnicity}
        onChange={(v) => this.handlechosenEthnicityChange?.(v)}
        options={[
          { value: null, label: "All" },
          { value: "Asian only", label: "Asian" },
          { value: "European only", label: "European" },
          { value: "European/Māori", label: "European/Māori" },
          { value: "Māori only", label: "Māori" },
          { value: "Pacific Peoples only", label: "Other" },
        ]}
      />

      <Box sx={{ width: "1px", height: 20, bgcolor: "rgba(0,0,0,0.12)" }} />

      <PillSwitcher
        label="Wave"
        layoutId="wave"
        value={this.state.waveSelect}
        onChange={(v) => this.handlewaveSelectChange?.(v)}
        options={(this.state.waveOptions || []).map((w) => ({
          value: w,
          label: `${w}`,
        }))}
      />
    </Box>
  </Box>

  {/* Plot area */}
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      overflow: "hidden",
    }}
  >
    <SegmentTreeMap
      metricRows={this.state.metricRows}
      selectedFeature={this.state.selectedFeature}
      waveSelect={this.state.waveSelect}
    />
  </Box>
</Box>
</Box>

  )
}




  }

export default withRouter(SegmentPage);